import React, { useState } from "react"
import { Close as CloseIcon } from "@mui/icons-material"

import { Dialog } from "@/components/Dialog"
import { Text } from "@/components/Text"
import { PrimitiveBox } from "@/primitives/PrimitiveBox"
import { PrimitiveCheckbox } from "@/primitives/PrimitiveCheckbox"
import { PrimitiveChip } from "@/primitives/PrimitiveChip"
import { PrimitiveDivider } from "@/primitives/PrimitiveDivider"
import { PrimitiveFormControlLabel } from "@/primitives/PrimitiveFormControlLabel"
import { PrimitiveIconButton } from "@/primitives/PrimitiveIconButton"
import { PrimitiveImage } from "@/primitives/PrimitiveImage"
import { colors } from "@/styles/colors"
import { AnnouncementPopupProps } from "@/containers/AnnouncementPopup/protocols"
import useStyles from "@/containers/AnnouncementPopup/styles"

export type { Announcement, AnnouncementPopupProps } from "@/containers/AnnouncementPopup/protocols"

export const AnnouncementPopup: React.FC<AnnouncementPopupProps> = ({
	announcement,
	open,
	onClose,
	onClosePermanently,
	onDoNotShowAgainChange,
	shouldHideDoNotShowAgain
}) => {
	const [isDoNotShowAgainChecked, setIsDoNotShowAgainChecked] = useState(false)

	const classes = useStyles()

	const ChipIcon = announcement?.chipIcon
	const Content = announcement?.content

	const handleDoNotShowAgainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked

		setIsDoNotShowAgainChecked(checked)

		onDoNotShowAgainChange?.(checked)
	}

	return (
		<Dialog.Container
			open={open}
			maxWidth="sm"
			fullWidth
			slotProps={{
				paper: {
					sx: {
						padding: "0 !important",
						borderRadius: "18px",
						margin: "1rem"
					}
				}
			}}
		>
			<PrimitiveBox
				sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column"
				}}
			>
				<PrimitiveBox sx={classes.header}>
					{announcement?.image && (
						<PrimitiveImage
							src={announcement.image}
							alt=""
							sx={classes.image as React.CSSProperties}
						/>
					)}

					{announcement?.chipText && (
						<PrimitiveChip
							label={announcement.chipText.toUpperCase()}
							size="small"
							icon={
								ChipIcon ? (
									<ChipIcon
										style={{
											color: "inherit",
											fontSize: "inherit"
										}}
									/>
								) : undefined
							}
							className={announcement?.chipClassName}
							sx={classes.badge}
						/>
					)}

					<PrimitiveIconButton
						onClick={onClosePermanently}
						sx={classes.closeButton}
						size="small"
					>
						<CloseIcon fontSize="small" />
					</PrimitiveIconButton>
				</PrimitiveBox>

				<PrimitiveBox
					sx={{
						padding: "24px 24px 16px 24px"
					}}
				>
					{announcement?.title && (
						<Text.Title
							type="h1"
							fontWeight="bold"
							color={colors.gray[900]}
							sx={{
								fontSize: "1.375rem",
								marginBottom: "8px"
							}}
						>
							{announcement.title}
						</Text.Title>
					)}

					{announcement?.body && (
						<Text.Body
							type="small1"
							color={colors.gray[700]}
							sx={{
								marginBottom: "16px"
							}}
						>
							{announcement.body}
						</Text.Body>
					)}

					{Content && (
						<PrimitiveBox sx={{ marginBottom: "16px" }}>
							<Content />
						</PrimitiveBox>
					)}

					<PrimitiveDivider />
				</PrimitiveBox>

				<PrimitiveBox
					sx={{
						padding: "16px 24px 24px 24px",
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						gap: "8px"
					}}
				>
					{!shouldHideDoNotShowAgain && (
						<PrimitiveFormControlLabel
							sx={{
								marginRight: "auto",
								flex: 1
							}}
							control={
								<PrimitiveCheckbox
									checked={isDoNotShowAgainChecked}
									onChange={handleDoNotShowAgainChange}
									color="primary"
									size="small"
								/>
							}
							label={
								<Text.Body
									type="small2"
									color={colors.gray[600]}
								>
									Não mostrar novamente
								</Text.Body>
							}
						/>
					)}

					<Dialog.CancelButton onClick={onClose} size="small">
						Fechar
					</Dialog.CancelButton>

					{announcement?.confirmButtonText && (
						<Dialog.ConfirmButton
							size="small"
							disabled={announcement?.isConfirmDisabled}
							onClick={announcement?.onAction ?? onClose}
						>
							{announcement.confirmButtonText}
						</Dialog.ConfirmButton>
					)}
				</PrimitiveBox>
			</PrimitiveBox>
		</Dialog.Container>
	)
}
